/**
 * Shared, framework-agnostic builder for the env vars a channel MCP server
 * (`~/.augmented/_mcp/<channel>-channel.js`) reads to enforce sender-gating and
 * peer collaboration. Both framework adapters spawn the SAME channel bundle
 * servers, so the env-var contract is identical; only WHERE the vars are written
 * differs (Claude Code `.mcp.json` `env` vs opencode `opencode.json`
 * `mcp.<id>.environment`).
 *
 * The canonical contract lives in the claude-code adapter's
 * `writeChannelCredentials` (`frameworks/claudecode/index.ts`) - this mirrors it
 * so the opencode adapter emits byte-identical env. The claude-code path is the
 * reference and a future refactor should dedupe onto this helper; until then the
 * `channel-env.test.ts` cases pin the names against that contract.
 *
 * Deliberately NOT covered here (they need channel bot-install config keys the
 * adapters resolve separately): `SLACK_ALLOWED_USERS`, and the `internal_only`
 * `<CHANNEL>_HOME_TEAM_ID` / `_HOME_TENANT_ID` org-boundary vars.
 */

import { CHANNEL_SECRET_ENV_KEYS } from './env-integrations-file.js';

export type PeerGatePath = 'same_team' | 'intra_org_unrestricted' | `grant:${string}` | null;

export interface SlackPeer {
  code_name: string;
  bot_user_id: string;
  agent_id: string;
  gate_path?: PeerGatePath;
}
export interface TelegramPeer {
  code_name: string;
  bot_id: number;
  agent_id: string;
  gate_path?: PeerGatePath;
}

export interface ChannelEnvOptions {
  /** @deprecated prefer `peerDisabled`; true folds to 'all'. */
  telegramPeerDisabled?: boolean;
  peerDisabled?: 'off' | 'cross_team_only' | 'all';
  telegramPeers?: ReadonlyArray<TelegramPeer>;
  slackPeers?: ReadonlyArray<SlackPeer>;
  slackTeamPeerUserIds?: ReadonlyArray<string>;
  agentTimezone?: string;
  agentAvatarUrl?: string;
  senderPolicy?: {
    mode: 'all' | 'agents_only' | 'team_only' | 'team_agents_only' | 'manager_only';
    team_id?: string;
    principal?: { slack_user_id?: string; telegram_chat_id?: string; teams_aad_object_id?: string };
    team_principals?: { slack_user_ids?: string[]; telegram_chat_ids?: string[]; teams_aad_object_ids?: string[] };
    internal_only?: boolean;
    source?: 'agent' | 'org';
  } | null;
}

export interface ChannelCredentialResult {
  /**
   * Env entries for the channel server: identifiers inline, and secret refs as
   * opencode `{env:VAR}` substitutions (Claude Code's `${VAR}` analogue) that
   * resolve from the process env the manager sources `.env.integrations` into.
   */
  env: Record<string, string>;
  /** Raw secret values to persist to `.env.integrations` (keyed by env-var name). */
  secrets: Record<string, string>;
}

/**
 * Map a channel's `config` to the credential env vars its bundle server reads,
 * mirroring the claude-code adapter's credential half. Secrets (bot tokens,
 * client secret, project api key) go to `.env.integrations` and are referenced
 * as `{env:VAR}`; identifiers (app id, tenant id, phone number id, base/version
 * overrides) stay inline. Unknown channels return empty.
 */
export function buildChannelCredentialEnv(
  channelId: string,
  config: Record<string, unknown>,
): ChannelCredentialResult {
  const env: Record<string, string> = {};
  const secrets: Record<string, string> = {};
  const str = (k: string): string | undefined => {
    const v = config[k];
    return typeof v === 'string' && v.trim() !== '' ? v : undefined;
  };
  const secret = (name: string, val: string | undefined): void => {
    if (val) { secrets[name] = val; env[name] = `{env:${name}}`; }
  };
  const literal = (name: string, val: string | undefined): void => {
    if (val) env[name] = val;
  };

  switch (channelId) {
    case 'telegram':
      secret('TELEGRAM_BOT_TOKEN', str('bot_token'));
      break;
    case 'slack':
      secret('SLACK_BOT_TOKEN', str('bot_token'));
      secret('SLACK_APP_TOKEN', str('app_token'));
      break;
    case 'msteams':
      literal('MSTEAMS_APP_ID', str('app_id'));
      secret('MSTEAMS_CLIENT_SECRET', str('client_secret'));
      env['MSTEAMS_TENANT_ID'] = str('tenant_id') ?? 'common';
      break;
    case 'whatsapp':
      secret('WHATSAPP_PROJECT_API_KEY', str('project_api_key'));
      literal('WHATSAPP_PHONE_NUMBER_ID', str('phone_number_id'));
      literal('WHATSAPP_KAPSO_BASE_URL', str('kapso_base_url'));
      literal('WHATSAPP_KAPSO_GRAPH_VERSION', str('kapso_graph_version'));
      break;
    default:
      break;
  }
  return { env, secrets };
}

/**
 * ENG-9350 (epic ENG-9347): collect the channel bot-token secrets across an
 * agent's active channel configs into a flat `ENV_KEY -> raw value` map, for
 * delivery into the Claude Code spawn env (`tmux new-session -e`) instead of the
 * on-disk `.env.integrations` file. Reuses {@link buildChannelCredentialEnv}'s
 * `secrets` so the KEYS + VALUES are byte-identical to what the file writer would
 * have persisted — the two delivery paths can't diverge.
 *
 * Scoped to {@link CHANNEL_SECRET_ENV_KEYS} (the fixed Slack/Telegram/Teams set):
 * WhatsApp's `WHATSAPP_PROJECT_API_KEY` is intentionally excluded — it is not in
 * that set (nor the ENG-6062 drift set), so this first slice leaves it on the
 * file.
 *
 * Eligibility MATCHES the manager's channel-credential writer exactly
 * (`manager-worker.ts`: `(status === 'active' || 'pending') && entry.config`),
 * so the spawn env carries a secret for EXACTLY the channels whose `.mcp.json`
 * `${VAR}` templates were written — a revoked/incomplete entry can't inject a
 * stale secret, and a written channel is never left with an empty `${VAR}`.
 *
 * `channelConfigs` is the `/host/refresh` `channel_configs` shape:
 * `channelId -> { config, status? }`. Pure; no fs, no side effects.
 */
export function collectChannelSpawnSecrets(
  channelConfigs: Record<string, { config?: unknown; status?: string } | undefined> | null | undefined,
): Record<string, string> {
  const allowed = new Set<string>(CHANNEL_SECRET_ENV_KEYS);
  const out: Record<string, string> = {};
  for (const [channelId, entry] of Object.entries(channelConfigs ?? {})) {
    if (!entry || !(entry.status === 'active' || entry.status === 'pending') || !entry.config) continue;
    const config = entry.config as Record<string, unknown>;
    const { secrets } = buildChannelCredentialEnv(channelId, config);
    for (const [key, value] of Object.entries(secrets)) {
      if (allowed.has(key)) out[key] = value;
    }
  }
  return out;
}

/** peer_agent_mode + peer_group_ids off the per-agent channel config, channel-prefixed. */
function peerModeEnv(prefix: string, config: Record<string, unknown>): Record<string, string> {
  const env: Record<string, string> = {};
  const mode = config['peer_agent_mode'];
  if (mode === 'listen' || mode === 'respond') env[`${prefix}_PEER_AGENT_MODE`] = mode;
  const rawGroupIds = config['peer_group_ids'];
  if (Array.isArray(rawGroupIds) && rawGroupIds.length > 0) {
    const ids = rawGroupIds
      .map((v) => (typeof v === 'string' || typeof v === 'number' ? String(v).trim() : ''))
      .filter((v) => v.length > 0);
    if (ids.length > 0) env[`${prefix}_PEER_GROUP_IDS`] = ids.join(',');
  }
  return env;
}

/**
 * Build the sender-gating / peer / tz env for a channel MCP server. Pure; the
 * caller merges the result into the channel server's environment alongside
 * credentials + PATH/HOME.
 */
export function buildChannelServerEnv(
  channelId: string,
  config: Record<string, unknown>,
  options?: ChannelEnvOptions,
): Record<string, string> {
  const env: Record<string, string> = {};

  // --- Common (channel-agnostic) ---
  const tz = options?.agentTimezone?.trim();
  if (tz) env['TZ'] = tz;

  const peerDisabledMode: 'off' | 'cross_team_only' | 'all' =
    options?.peerDisabled ?? (options?.telegramPeerDisabled === true ? 'all' : 'off');
  if (peerDisabledMode !== 'off') env['PEER_DISABLED'] = peerDisabledMode;

  const mode = options?.senderPolicy?.mode;
  // team_id is shared by the three modes whose agent-axis check needs it.
  if ((mode === 'team_agents_only' || mode === 'manager_only' || mode === 'team_only') && options?.senderPolicy?.team_id) {
    env['AGT_TEAM_ID'] = options.senderPolicy.team_id;
  }

  // --- Slack ---
  if (channelId === 'slack') {
    Object.assign(env, peerModeEnv('SLACK', config));
    if (options?.slackPeers && options.slackPeers.length > 0) {
      env['SLACK_PEERS'] = JSON.stringify(
        options.slackPeers.map((p) => ({ code_name: p.code_name, bot_user_id: p.bot_user_id, agent_id: p.agent_id })),
      );
      const gate = options.slackPeers.filter((p) => p.gate_path !== undefined).map((p) => [p.bot_user_id, p.gate_path] as const);
      if (gate.length > 0) env['SLACK_PEERS_GATE'] = JSON.stringify(Object.fromEntries(gate));
    }
    if (options?.slackTeamPeerUserIds && options.slackTeamPeerUserIds.length > 0) {
      env['SLACK_TEAM_PEER_USER_IDS'] = options.slackTeamPeerUserIds.join(',');
    }
    if (mode) env['SLACK_SENDER_POLICY'] = mode;
    if (mode === 'manager_only' && options?.senderPolicy?.principal?.slack_user_id) {
      env['SLACK_SENDER_POLICY_PRINCIPAL_ID'] = options.senderPolicy.principal.slack_user_id;
    }
    if (mode === 'team_only' && options?.senderPolicy?.team_principals?.slack_user_ids?.length) {
      env['SLACK_SENDER_POLICY_TEAM_PRINCIPAL_IDS'] = options.senderPolicy.team_principals.slack_user_ids.join(',');
    }
    const avatar = options?.agentAvatarUrl?.trim();
    if (avatar) env['SLACK_AGENT_AVATAR_URL'] = avatar;
    // ENG-7840: ack/skip reaction emoji for the manager-side opencode Slack
    // ingest (parity with the claude slack-channel MCP's SLACK_ACK_REACTION).
    // The ingest adds ack on admit ("seen, working on it") and skip on an
    // admitted no-reply. Emitted only when set so unset configs stay bare.
    const ackReaction = typeof config['ack_reaction'] === 'string' ? config['ack_reaction'].trim() : '';
    if (ackReaction) env['SLACK_ACK_REACTION'] = ackReaction;
    const skipReaction = typeof config['skip_reaction'] === 'string' ? config['skip_reaction'].trim() : '';
    if (skipReaction) env['SLACK_SKIP_REACTION'] = skipReaction;
    return env;
  }

  // --- Telegram ---
  if (channelId === 'telegram') {
    Object.assign(env, peerModeEnv('TELEGRAM', config));
    if (options?.telegramPeers && options.telegramPeers.length > 0) {
      env['TELEGRAM_PEERS'] = JSON.stringify(
        options.telegramPeers.map((p) => ({ code_name: p.code_name, bot_id: p.bot_id, agent_id: p.agent_id })),
      );
      const gate = options.telegramPeers.filter((p) => p.gate_path !== undefined).map((p) => [String(p.bot_id), p.gate_path] as const);
      if (gate.length > 0) env['TELEGRAM_PEERS_GATE'] = JSON.stringify(Object.fromEntries(gate));
    }
    // Legacy mirror: the old shape only expressed the kill-all case.
    if (peerDisabledMode === 'all') env['TELEGRAM_PEER_DISABLED'] = 'true';
    // ENG-7851: org-boundary chat allowlist for the manager-side opencode ingest
    // (parity with the claude telegram-channel MCP's TELEGRAM_ALLOWED_CHATS).
    // config['allowed_chats'] is a string[] of chat ids; empty ⇒ no chat gating.
    const rawAllowedChats = config['allowed_chats'];
    if (Array.isArray(rawAllowedChats)) {
      const chats = rawAllowedChats
        .map((v) => (typeof v === 'string' || typeof v === 'number' ? String(v).trim() : ''))
        .filter((v) => v.length > 0);
      if (chats.length > 0) env['TELEGRAM_ALLOWED_CHATS'] = chats.join(',');
    }
    // CS-1628: opt-in "private chats only". Emitted ONLY when the config says
    // true, so an agent that has never heard of the setting keeps exactly
    // today's behaviour rather than silently starting to drop group traffic.
    if (config['private_chats_only'] === true) {
      env['TELEGRAM_PRIVATE_CHATS_ONLY'] = '1';
    }
    // ENG-7851: ack/skip reaction emoji for the manager-side opencode Telegram
    // ingest (parity with the claude telegram-channel MCP's TELEGRAM_ACK_REACTION).
    const tgAckReaction = typeof config['ack_reaction'] === 'string' ? config['ack_reaction'].trim() : '';
    if (tgAckReaction) env['TELEGRAM_ACK_REACTION'] = tgAckReaction;
    const tgSkipReaction = typeof config['skip_reaction'] === 'string' ? config['skip_reaction'].trim() : '';
    if (tgSkipReaction) env['TELEGRAM_SKIP_REACTION'] = tgSkipReaction;
    return env;
  }

  // --- MS Teams ---
  if (channelId === 'msteams') {
    if (mode) env['MSTEAMS_SENDER_POLICY'] = mode;
    if (mode === 'manager_only' && options?.senderPolicy?.principal?.teams_aad_object_id) {
      env['MSTEAMS_SENDER_POLICY_PRINCIPAL_ID'] = options.senderPolicy.principal.teams_aad_object_id;
    }
    if (mode === 'team_only' && options?.senderPolicy?.team_principals?.teams_aad_object_ids?.length) {
      env['MSTEAMS_SENDER_POLICY_TEAM_PRINCIPAL_IDS'] = options.senderPolicy.team_principals.teams_aad_object_ids.join(',');
    }
    return env;
  }

  return env;
}
